import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, {
  PlanChangeRequestRow,
  PlanChangeRequestListResponse,
  approvePlanChangeRequest,
  rejectPlanChangeRequest,
  extractApiError,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Operator-facing queue for tenant-filed plan-change requests.
 *
 * Backend surfaces:
 *   - GET    /api/tenants/plan-change-requests?status=
 *   - PUT    /api/tenants/:slug/plan                    (approve path)
 *   - POST   /api/tenants/:slug/plan-change-request/:auditId/reject
 *
 * The queue is operator-visible and the actions are destructive
 * (approve fires AWX, reject closes the request) — both actions
 * use a clear confirmation modal but NOT typed-confirm. Typed-
 * confirm is reserved for batch destructive actions per the
 * pattern landed in #1087; single-row approve/reject doesn't
 * warrant the extra friction.
 *
 * Federation badge:
 *   - green ("local")  → from the central Postgres
 *   - blue  ("<slug>") → federated from a scoped Enterprise instance
 *
 * The list response carries `federationSource` per row from the
 * backend; we never trust the row's own `tenantSlug` for the badge
 * since a misconfigured scoped instance could otherwise spoof the
 * source.
 */

type StatusFilter = 'pending' | 'resolved' | 'all';

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  pending: 'Offen',
  resolved: 'Erledigt',
  all: 'Alle',
};

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  resolved: 'bg-slate-200 text-slate-700',
};

const RESOLUTION_BADGE: Record<string, string> = {
  'plan-change': 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

export function PlanChangeRequestsPage() {
  const { operator } = useAuth();
  const [rows, setRows] = useState<PlanChangeRequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [approveRow, setApproveRow] = useState<PlanChangeRequestRow | null>(null);
  const [rejectRow, setRejectRow] = useState<PlanChangeRequestRow | null>(null);

  const isReadOnly = !!operator?.readOnly;

  const refresh = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      return api
        .get<PlanChangeRequestListResponse>('/tenants/plan-change-requests', {
          params: { status: filter, limit: 200 },
        })
        .then((r) => {
          setRows(r.data.rows);
          setTotal(r.data.total);
          setError(null);
        })
        .catch((err) => setError(extractApiError(err)))
        .finally(() => {
          if (!opts?.silent) setLoading(false);
        });
    },
    [filter],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === 'pending').length,
    [rows],
  );
  const resolvedCount = useMemo(
    () => rows.filter((r) => r.status === 'resolved').length,
    [rows],
  );

  async function handleApprove(row: PlanChangeRequestRow, note: string): Promise<void> {
    try {
      await approvePlanChangeRequest(
        row.tenantSlug,
        row.requestedPlan as 'basic' | 'business' | 'enterprise',
        note,
      );
      setApproveRow(null);
      // Refresh so the row flips to resolved on the next list
      // read. Plan history materialises asynchronously (AWX kicks
      // the playbook + writes the TenantPlanHistory row) but the
      // initiating call already records the audit on this path;
      // the list derivation needs the plan-history row, which is
      // written synchronously by changePlan() before the response
      // returns.
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  async function handleReject(row: PlanChangeRequestRow, reason: string): Promise<void> {
    try {
      await rejectPlanChangeRequest(row.tenantSlug, row.auditId, reason);
      setRejectRow(null);
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Plan-Änderungs-Anfragen</h2>
          <p className="text-sm text-slate-500">
            Tenant-eingereichte Plan-Wechsel via{' '}
            <code className="text-xs">POST /api/me/tenant/plan-change-request</code>.
            Approve startet den AWX-Job (
            <code className="text-xs">tenant-plan-change</code>); Reject schliesst
            die Anfrage ohne Plan-Wechsel.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-2 py-1 rounded border"
            aria-label="Status-Filter"
          >
            {(['pending', 'resolved', 'all'] as StatusFilter[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_FILTER_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Offen (in Ansicht)" value={pendingCount} tone="amber" />
        <StatCard label="Erledigt (in Ansicht)" value={resolvedCount} tone="slate" />
        <StatCard label="Insgesamt" value={total} tone="slate" />
      </div>

      {error && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 mb-4 text-sm"
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500">Lade…</p>
      ) : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left uppercase tracking-wide">
            <tr>
              <th className="p-3">Tenant</th>
              <th className="p-3">Aktueller Plan</th>
              <th className="p-3">Gewünscht</th>
              <th className="p-3">Eingereicht von</th>
              <th className="p-3">Eingereicht</th>
              <th className="p-3">Grund</th>
              <th className="p-3">Status</th>
              <th className="p-3">Quelle</th>
              <th className="p-3 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.federationSource}|${r.auditId}`} className="border-t hover:bg-slate-50 align-top">
                <td className="p-3 font-mono">
                  <Link
                    to={`/tenants/${r.tenantSlug}`}
                    className="text-primary-600 hover:underline"
                  >
                    {r.tenantSlug}
                  </Link>
                  <span className="block text-slate-500 text-xs">{r.tenantName}</span>
                </td>
                <td className="p-3">
                  <span className="badge bg-slate-200">{r.currentPlan}</span>
                </td>
                <td className="p-3">
                  <span className="badge bg-blue-100 text-blue-800">{r.requestedPlan}</span>
                </td>
                <td className="p-3 text-slate-600">{r.requestedByEmail}</td>
                <td className="p-3 text-slate-500" title={new Date(r.requestedAt).toLocaleString()}>
                  {relativeAge(r.requestedAt)}
                </td>
                <td className="p-3 text-slate-600 max-w-xs">
                  <span className="line-clamp-2" title={r.reason}>
                    {r.reason || <span className="italic text-slate-400">—</span>}
                  </span>
                </td>
                <td className="p-3">
                  <StatusCell row={r} />
                </td>
                <td className="p-3">
                  <FederationBadge source={r.federationSource} />
                </td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  {r.status === 'pending' ? (
                    <>
                      <button
                        onClick={() => setApproveRow(r)}
                        disabled={isReadOnly}
                        className="px-2 py-1 rounded bg-green-600 text-white text-xs hover:bg-green-700 disabled:opacity-40"
                        title={isReadOnly ? 'Read-only-Operator' : 'Plan-Wechsel auslösen'}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectRow(r)}
                        disabled={isReadOnly}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-40"
                        title={isReadOnly ? 'Read-only-Operator' : 'Anfrage ablehnen'}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="p-6 text-center text-slate-500">
                  Keine Anfragen für diesen Filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {approveRow && (
        <ApproveModal
          row={approveRow}
          onCancel={() => setApproveRow(null)}
          onConfirm={(note) => handleApprove(approveRow, note)}
        />
      )}
      {rejectRow && (
        <RejectModal
          row={rejectRow}
          onCancel={() => setRejectRow(null)}
          onConfirm={(reason) => handleReject(rejectRow, reason)}
        />
      )}
    </div>
  );
}

// ===================================================================
// Counter badge for the NavLink. Polls the pending-count endpoint
// every 30s — cheap (the queue is rarely > 20 items) and gives
// operators an at-a-glance "things waiting on you" signal without
// having to land on the page. Exported here so App.tsx can mount
// it next to the NavLink without re-implementing the poll.
// ===================================================================

const PCR_BADGE_POLL_INTERVAL_MS = 30_000;

export function PlanChangeRequestsNavBadge() {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    function load() {
      api
        .get<PlanChangeRequestListResponse>('/tenants/plan-change-requests', {
          // Cheap probe — we only need the count, but the list
          // endpoint doesn't expose a HEAD/count variant. Cap at
          // 1 to minimise transferred bytes; `total` carries the
          // true count.
          params: { status: 'pending', limit: 1 },
        })
        .then((r) => {
          if (cancelled) return;
          setCount(r.data.total);
        })
        .catch(() => {
          // Silent on poll failures — the badge isn't critical UX
          // and surfacing an error here would compete with the
          // main page's error banner.
          if (!cancelled) setCount(null);
        });
    }
    load();
    const id = window.setInterval(load, PCR_BADGE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (count === null || count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
      aria-label={`${count} offene Plan-Änderungs-Anfragen`}
      title={`${count} offene Plan-Änderungs-Anfragen`}
    >
      {count}
    </span>
  );
}

// -------------------------------------------------------------------
// Subcomponents.
// -------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: number;
  tone: 'slate' | 'amber';
}

function StatCard({ label, value, tone }: StatCardProps) {
  const toneClass = tone === 'amber' ? 'text-amber-700' : 'text-slate-800';
  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-3xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function StatusCell({ row }: { row: PlanChangeRequestRow }) {
  if (row.status === 'pending') {
    return <span className={`badge ${STATUS_BADGE.pending}`}>pending</span>;
  }
  const kind = row.resolutionKind ?? 'plan-change';
  const label = kind === 'rejected' ? 'rejected' : 'approved';
  const cls = RESOLUTION_BADGE[kind] ?? STATUS_BADGE.resolved;
  return (
    <div>
      <span className={`badge ${cls}`}>{label}</span>
      {row.resolvedAt && (
        <div
          className="text-xs text-slate-500 mt-1"
          title={new Date(row.resolvedAt).toLocaleString()}
        >
          {relativeAge(row.resolvedAt)}
        </div>
      )}
      {row.rejectionReason && (
        <div className="text-xs text-slate-500 mt-1 italic" title={row.rejectionReason}>
          „{row.rejectionReason}"
        </div>
      )}
    </div>
  );
}

function FederationBadge({ source }: { source: string }) {
  if (source === 'local') {
    return (
      <span className="badge bg-green-100 text-green-800" title="Aus zentraler Postgres">
        local
      </span>
    );
  }
  return (
    <span
      className="badge bg-blue-100 text-blue-800"
      title={`Föderiert vom Scoped-Instance "${source}"`}
    >
      {source}
    </span>
  );
}

interface ApproveModalProps {
  row: PlanChangeRequestRow;
  onCancel: () => void;
  onConfirm: (note: string) => void | Promise<void>;
}

function ApproveModal({ row, onCancel, onConfirm }: ApproveModalProps) {
  const [note, setNote] = useState(`Approved per tenant request (audit ${row.auditId})`);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    try {
      await onConfirm(note);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell onClose={onCancel} ariaLabel="Plan-Wechsel bestätigen">
      <h3 className="text-lg font-semibold mb-2">Plan-Wechsel auslösen?</h3>
      <p className="text-sm text-slate-600 mb-4">
        Trigger plan change from <span className="font-mono">{row.currentPlan}</span> to{' '}
        <span className="font-mono">{row.requestedPlan}</span> for{' '}
        <span className="font-mono">{row.tenantSlug}</span>?
        <br />
        Dies startet den AWX-Job <code className="text-xs">tenant-plan-change</code> und
        ändert tenant.plan in der zentralen Postgres.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Operator-Notiz (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={3}
            className="mt-1 w-full px-2 py-1 border rounded text-sm"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1 rounded border"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            {busy ? 'Starte…' : 'Approve & kick AWX'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

interface RejectModalProps {
  row: PlanChangeRequestRow;
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

function RejectModal({ row, onCancel, onConfirm }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell onClose={onCancel} ariaLabel="Plan-Anfrage ablehnen">
      <h3 className="text-lg font-semibold mb-2">Anfrage ablehnen?</h3>
      <p className="text-sm text-slate-600 mb-4">
        Schliesst die Plan-Anfrage von <span className="font-mono">{row.tenantSlug}</span>{' '}
        ({row.currentPlan} → {row.requestedPlan}) <strong>ohne</strong> einen
        Plan-Wechsel auszulösen. Wird im Audit-Log als{' '}
        <code className="text-xs">plan.change.rejected</code> festgehalten.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Ablehnungsgrund (erforderlich)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            required
            placeholder="z.B. Tenant nicht enterprise-eligible — Billing on hold"
            className="mt-1 w-full px-2 py-1 border rounded text-sm"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1 rounded border"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || !reason.trim()}
            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Lehne ab…' : 'Ablehnen'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

interface ModalShellProps {
  children: React.ReactNode;
  onClose: () => void;
  ariaLabel: string;
}

function ModalShell({ children, onClose, ariaLabel }: ModalShellProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-lg p-5">{children}</div>
    </div>
  );
}

function relativeAge(iso: string | null): string {
  if (!iso) return '—';
  const delta = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(delta)) return iso;
  if (delta < 0) return 'in the future';
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
